"use client"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import React, { CSSProperties, ReactNode } from "react"
import { useInView } from "react-intersection-observer"
import { transitionDelayTime, useIsDesktop } from "../../index"
import { colors, mediaQueries, sizes } from "../../theme/theme"
import { useTransitionUpStyles } from "../../transitions/useTransitionUpStyles"
import { useInViewConfig } from "../../transitions/utils"
import EnhancedTypography from "./EnhancedTypography"

export interface CommonTitleProps {
  children?: ReactNode
  mobileTitle?: string
  title?:
    | string
    | { value: string }
    | { valueType: string; value: string }
    | any
  subtitle?:
    | string
    | { value: string }
    | { valueType: string; value: string }
    | any
  text?:
    | string
    | { value: string }
    | { richText: string }
    | { valueType: string; value: string }
    | any
  mobileSubtitle?: string
  additionalStylesH2?: CSSProperties
  additionalStylesWrapper?: CSSProperties
  className?: string
  titleComponent?: React.ElementType
}

const StyledTextsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "additionalstyleswrapper",
})<{
  additionalstyleswrapper?: CSSProperties
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  h4 {
    font-weight: 400;
  }
  ${mediaQueries.mobile} {
    margin-top: 24px;
    gap: ${sizes.baseDouble};
  }
  ${(props) =>
    props.additionalstyleswrapper &&
    Object.entries(props.additionalstyleswrapper)
      .map(
        ([key, value]) =>
          `${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}:${value};`,
      )
      .join("\n")}
`
const StyledMainTitle = styled(Typography)<{ component?: React.ElementType }>`
  color: ${colors.black};
  ${mediaQueries.tabletDown} {
    font-size: 31px;
    line-height: 37px;
    font-weight: 700;
  }
`
const StyledSubTitle = styled(EnhancedTypography)`
  color: ${colors.red};
  ${mediaQueries.tabletDown} {
    font-size: 19px;
    line-height: 26.6px;
    font-weight: 400;
  }
`

// Helper function to safely extract text content from various formats
const extractTextValue = (content: any): string | undefined => {
  if (!content) return undefined
  if (typeof content === "string") return content
  if (typeof content === "object") {
    // Handle most common patterns first
    if ("value" in content) return content.value
    if ("richText" in content) return content.richText

    // Fall back to string conversion if necessary
    try {
      return String(content)
    } catch {
      return undefined
    }
  }
  return undefined
}

const CommonTitle: React.FC<CommonTitleProps> = ({
  title,
  mobileTitle,
  subtitle,
  mobileSubtitle,
  text,
  children,
  additionalStylesH2,
  additionalStylesWrapper,
  className,
  titleComponent,
}) => {
  const { ref, inView } = useInView(useInViewConfig)
  const transitionStyle = useTransitionUpStyles(inView, transitionDelayTime)
  const transitionStyleTwo = useTransitionUpStyles(
    inView,
    transitionDelayTime * 2,
  )
  const transitionStyleThree = useTransitionUpStyles(
    inView,
    transitionDelayTime * 3,
  )
  const isDesktop = useIsDesktop()

  // Extract actual text values from props
  const subtitleText =
    !isDesktop && mobileSubtitle ? mobileSubtitle : extractTextValue(subtitle)

  const titleText =
    !isDesktop && mobileTitle ? mobileTitle : extractTextValue(title)

  const contentText = extractTextValue(text)
  return (
    <StyledTextsContainer
      additionalstyleswrapper={additionalStylesWrapper}
      className={className}
    >
      {(subtitle || mobileSubtitle) && (
        <StyledSubTitle
          ref={ref}
          style={
            {
              ...transitionStyle,
              ...additionalStylesH2,
            } as React.CSSProperties
          }
          variant="h3"
          component="p"
          className="subtitle"
        >
          {subtitleText}
        </StyledSubTitle>
      )}

      <StyledMainTitle
        ref={ref}
        style={
          {
            ...transitionStyleTwo,
            ...additionalStylesH2,
          } as React.CSSProperties
        }
        variant="h2"
        className="title"
        component={titleComponent || undefined}
      >
        {titleText || children}
      </StyledMainTitle>

      {text && (
        <EnhancedTypography
          ref={ref}
          style={
            {
              ...transitionStyleThree,
              ...additionalStylesH2,
            } as React.CSSProperties
          }
          variant="h4"
          component={titleText || children ? "p" : "h3"}
          className="text"
        >
          {contentText}
        </EnhancedTypography>
      )}
    </StyledTextsContainer>
  )
}

export default CommonTitle
